import { useMemo, useState } from 'react';

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Shown once, and the only screen in the app that cannot be revisited.
 *
 * It used to be twenty-four words and a button saying "I have written it
 * down", which is a claim rather than a fact — the cheapest path through was
 * to record nothing and end up one lost device from an archive nobody can
 * recover. So: the words are numbered, offered as a file, and three of them
 * have to come back before this screen will let go.
 */
export function RecoveryCode({ words, googleAvailable, onConfirmed }: {
  words: string[];
  googleAvailable: boolean;
  onConfirmed: () => void;
}) {
  // Chosen once, so re-rendering cannot reshuffle the question mid-answer.
  const asked = useMemo(() => {
    const picks = new Set<number>();
    while (picks.size < 3 && picks.size < words.length) {
      picks.add(Math.floor(Math.random() * words.length));
    }
    return [...picks].sort((a, b) => a - b);
  }, [words]);

  const [typed, setTyped] = useState<string[]>(['', '', '']);
  const [checked, setChecked] = useState(false);

  const right = asked.map((w, i) => norm(typed[i] ?? '') === norm(words[w] ?? ''));
  const allRight = right.every(Boolean);

  const save = () => {
    const body = [
      'OkaZip — recovery code',
      '',
      ...words.map((w, i) => `${String(i + 1).padStart(2, ' ')}. ${w}`),
      '',
      'These twenty-four words are the only way back into your archive if you',
      'lose every device with a passkey. Nobody can reset them and nobody can',
      'recover your messages without them.',
      '',
      'Keep this somewhere a stranger cannot reach.',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'okazip-recovery-code.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="gate wide">
      <h1>Write this down</h1>
      <p>These twenty-four words are the only way back into your archive if you lose
         this device. We cannot reset them and we cannot recover your messages
         without them — nobody can, including us.</p>

      <ol className="words">
        {words.map((w, i) => (
          <li key={i}><span className="n">{i + 1}</span>{w}</li>
        ))}
      </ol>

      <div className="actions">
        <button className="ghost" onClick={save}>Save as a text file</button>
      </div>

      {googleAvailable && (
        <p className="fine">If you lose this device you will also need a way to tell us which
           archive is yours — link a Google account from your profile.</p>
      )}

      <h2 className="check-h">Now prove you have it</h2>
      <p className="fine">Type these three words back. We ask because the alternative is
         finding out you did not record them on the day it matters.</p>

      <div className="confirm">
        {asked.map((w, i) => (
          <label key={w} htmlFor={`confirm-${i}`}>
            <span className="which">Word {w + 1}</span>
            <input id={`confirm-${i}`} data-word={w + 1} autoComplete="off"
                   spellCheck={false} value={typed[i]}
                   className={checked && !right[i] ? 'bad' : right[i] ? 'good' : ''}
                   onChange={(e) => {
                     const next = [...typed];
                     next[i] = e.target.value;
                     setTyped(next);
                   }} />
          </label>
        ))}
      </div>

      {checked && !allRight && (
        <p className="err">That does not match. Check the numbers against your copy.</p>
      )}

      <button onClick={() => (allRight ? onConfirmed() : setChecked(true))}>
        Continue
      </button>
    </div>
  );
}
